import {
	APP_BASE_HREF,
	CommonModule,
	DecimalPipe,
	Location,
	LocationStrategy,
	PathLocationStrategy,
} from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
	FaIconLibrary,
	FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { faFrown } from '@fortawesome/free-regular-svg-icons';
import {
	faAngleDown,
	faAngleLeft,
	faAngleRight,
	faAsterisk,
	faBars,
	faCalendarPlus,
	faCamera,
	faCheck,
	faCheckCircle,
	faCheckSquare,
	faChevronDown,
	faChevronLeft,
	faChevronRight,
	faCircle,
	faClock,
	faComments,
	faEnvelope,
	faExclamationCircle,
	faExclamationTriangle,
	faInfoCircle,
	faLock,
	faPaperPlane,
	faPauseCircle,
	faPlus,
	faPlusCircle,
	faSave,
	faSearch,
	faSortDown,
	faSortUp,
	faSync,
	faTimes,
	faTimesCircle,
	faToggleOff,
	faToggleOn,
	faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import {
	FreudAvatarModule,
	FreudButtonModule,
	FreudConfirmDialogModule,
	FreudLoaderModule,
	FreudRadioButtonModule,
	FreudSelectButtonModule,
	FreudSplitButtonModule,
	FreudSwitchModule,
	FreudTagModule,
	FreudToastModule,
	FreudTooltipModule,
} from '@freud-ds/web-components';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OfficeIconModule } from '@office-ui/icons/icon.component';
import { OfficeModalModule } from '@office-ui/modal/modal.module';
// Error handling
import {
	InjectableRxStompConfig,
	RxStompService,
	rxStompServiceFactory,
} from '@stomp/ng2-stompjs';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import {
	ZkButtonModule,
	ZkDialogModule,
	ZkIconModule,
	ZkInputModule,
	ZkLoaderModule,
	ZkNotificationModule,
	ZkTextModule,
} from '@zenklub/ng-ui';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { ClientsAddComponent } from 'app/clients/add/clients-add.component';
import { ClientsComponent } from 'app/clients/clients.component';
import { ClientsDetailsComponent } from 'app/clients/details/clients-details.component';
import { ClientsEditComponent } from 'app/clients/edit/clients-edit.component';
// Zenklub pipes.
import { ArraySortPipe } from 'app/pipes/array/sortarray.pipe';
import {
	FullDatePipe,
	InFullDatePipe,
} from 'app/pipes/datetime/infulldate.pipe';
import { ShortTimePipe } from 'app/pipes/datetime/shorttime.pipe';
import { Nl2brPipe } from 'app/pipes/text/nl2br.pipe';
import player from 'lottie-web';
import { AddressCorporativePriceModule } from 'modules/address/components/address-coorporative-price/address-coorporative-price.module';
import { AddressParticularPriceModule } from 'modules/address/components/address-particular-price/address-particular-price.module';
import { AppointmentPreviewListModule } from 'modules/appointments/components/appointment-preview-list/appointment-preview-list.module';
import { ContactsModule } from 'modules/contacts/contacts.module';
import { SentryErrorHandler } from 'modules/core/sentry/sentry-error-handler';
import { PromptuaryExportModule } from 'modules/exports/pages/promptuary-export/promptuary-export.module';
import { InsightsModule } from 'modules/insights/insights.module';
import { PromptuarySidebarModule } from 'modules/promptuary/components/promptuary-sidebar/promptuary-sidebar.module';
import { SpeechToTextModule } from 'modules/promptuary/components/speech-to-text/speech-to-text.module';
import { PromptuaryModule } from 'modules/promptuary/promptuary.module';
import { HeaderModule } from 'modules/shell/components/header/header.module';
import { SessionService } from 'modules/video-conference/services/session/session.service';
import { VideoConferenceModule } from 'modules/video-conference/video-conference.module';
import { Ng2CompleterModule } from 'ng2-completer';
import {
	CURRENCY_MASK_CONFIG,
	CurrencyMaskConfig,
	CurrencyMaskModule,
} from 'ng2-currency-mask';
// Third party (Bootstrap).
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ImageCropperModule } from 'ngx-image-cropper';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { LottieModule } from 'ngx-lottie';
import { NgxMaskModule } from 'ngx-mask';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChartModule } from 'primeng/chart';
import { DropdownModule } from 'primeng/dropdown';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { NgxUnleashProxyClientModule } from 'shared/unleash/ngx-unleash-proxy-client.module';

import { ExperimentModule } from './../modules/experiments/experiments.module';
import { PromptuaryPendenciesModule } from './../modules/promptuary/components/promptuary-pendencies/promptuary-pendencies.module';
import { environment } from '../environments/environment';
import { AppointmentDetailsSidebarModule } from '../modules/appointments/components/appointment-details-sidebar/appointment-details-sidebar.module';
import { AppointmentSidebarModule } from '../modules/appointments/components/appointment-sidebar/appointment-sidebar.module';
import { AppointmentTypeBadgeModule } from '../modules/appointments/components/appointment-type-badge/appointment-type-badge.module';
import { AccountComponent } from './account/account.component';
import { AccountMobPaymentInfoComponent } from './account/mobile/paymentinfo/mob-paymentinfo.component';
import { AccountPaymentInfoComponent } from './account/paymentinfo/paymentinfo.component';
import { SignatureEditorComponent } from './account/paymentinfo/signature-editor/signature-editor.component';
import { AccountPremiumComponent } from './account/premium/premium.component';
import { ProfileCompletionModule } from './account/profile-completion/profile-completion.module';
import { AppComponent } from './app.component';
import { HttpLoaderFactory } from './app.http';
import { AppRoutes } from './app.routes';
import { AppointmentsComponent } from './appointments/appointments.component';
import { DetailsContactInfoModule } from './appointments/details-contact-info/details-contact-info.module';
import { DetailsComponent } from './appointments/details/details.component';
import { GoogleCalendar } from './appointments/google-calendar/google-calendar.component';
import { MobAppointmentsManageComponent } from './appointments/mobile/manage/mobappointments-manage.component';
import { MobAppointmentsOptionsComponent } from './appointments/mobile/options/mobappointments-options.component';
import { ReasonsModalModule } from './appointments/reasons-modal/reasons-modal.module';
import { ReasonsComponent } from './appointments/reasons/reasons.component';
import { ReceiptButtonModule } from './appointments/receipt-button/receipt-button.module';
import { AdminLoginComponent } from './authentication/admin/admin.component';
import { AuthGuard } from './authentication/guard/auth.guard';
import { MyAccountCanDeactivate } from './authentication/guard/my-account-can-deactivate.guard';
import { LoginComponent } from './authentication/login/login.component';
import { LogoutComponent } from './authentication/logout/logout.component';
import { AvailabilitiesComponent } from './availabilities/availabilities.component';
import { ListAvailabilitiesComponent } from './availabilities/list-availabilities/list-availabilities.component';
import { ManageAvailabilitiesComponent } from './availabilities/manage-availabilities/manage-availabilities.component';
import { MobAvailabilitiesEditComponent } from './availabilities/mobile/edit-availability/mobavailabilities-edit.component';
import { MobAvailabilitiesListComponent } from './availabilities/mobile/list-availabilities/mobavailabilities-list.component';
import { MobAvailabilitiesManageComponent } from './availabilities/mobile/manage-availabilities/mobavailabilities-manage.component';
import { CalendarEventIconComponent } from './calendar/calendar-event-icon/calendar-event-icon.component';
import { CalendarFooterComponent } from './calendar/calendar-footer/calendar-footer.component';
import { CalendarHeaderComponent } from './calendar/calendar-header/calendar-header.component';
import { CalendarRecurrencyAttemptModalModule } from './calendar/calendar-recurrency-attempt-modal/calendar-recurrency-attempt-modal.module';
import { CalendarSlotRecurrencyAttemptComponent } from './calendar/calendar-slot-recurency-attempt/calendar-slot-recurency-attempt.component';
import { CalendarComponent } from './calendar/calendar.component';
import { DaycalendarComponent } from './calendar/content/daycalendar/daycalendar.component';
import { MonthcalendarComponent } from './calendar/content/monthcalendar/monthcalendar.component';
import { WeekcalendarComponent } from './calendar/content/weekcalendar/weekcalendar.component';
import { ChartCompensationComponent } from './charts/compensation/chart-compensation.component';
import { ChatComponent } from './chat/chat.component';
import { CheckoutModule } from './checkout/checkout.module';
import { FinancialsComponent } from './financials/financials.component';
import { HasAppointment } from './guards/hasappointment';
import { LookerEmbedComponent } from './looker/looker-embed.component';
import { MyAccountModule } from './my-account/my-account.module';
import { NotificationsComponent } from './notifications/notifications.component';
import { SafePipeModule } from './pipes/safe.pipe.module';
// Zenklub services.
import { ActionTrackerService } from './services/actiontracker.service';
import { AddressService } from './services/address.service';
import { ApiService } from './services/api.service';
import { AuthenticationService } from './services/authentication.service';
import { AvailabilitiesService } from './services/availabilities.service';
import { CacheService } from './services/cache.service';
import { CalendarService } from './services/calendar.service';
import { ChatService } from './services/chat.service';
import { CheckoutService } from './services/checkout.service';
import { ContactsService } from './services/contacts.service';
import { CurrencyService } from './services/currency.service';
import { DataLayerService } from './services/datalayer.service';
import { DateTimeService } from './services/datetime.service';
import { ErrorService } from './services/error.service';
import { EventsService } from './services/events.service';
import { FeedNotificationService } from './services/feed-notifications.service';
import { GlobalService } from './services/global.service';
import { IdWallService } from './services/id-wall.service';
import { AuthInterceptorModule } from './services/interceptor.service';
import { LocationService } from './services/location.service';
import { NetworkService } from './services/network.service';
import { NotificationsService } from './services/notifications.service';
import { SearchService } from './services/search.service';
import { StatsService } from './services/stats.service';
import { StompService } from './services/stomp.service';
import { StripeService } from './services/stripe.service';
import { SubscriptionService } from './services/subscription.service';
import { TaxonomyService } from './services/taxonomy.service';
import { TriageService } from './services/triage.service';
import { UserService } from './services/user.service';
import { WePayOutService } from './services/wepayout.service';
import { ZkuService } from './services/zku.service';
// Shared components
import { ClientEventList } from './shared/client-event-list/client-event-list.component';
import { ClientSearch } from './shared/client-search/client-search.component';
import { FinancialReportLookerComponent } from './shared/financial-report-looker/financial-report-looker.component';
import { InfoIconTooltipComponent } from './shared/info-icon-tooltip/info-icon-tooltip.component';
import { ValidationMessagesComponent } from './shared/validation-message/validation-messages.component';
import { SignupModule } from './signup/signup.module';

export function playerFactory() {
	return player;
}

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
	align: 'right',
	allowNegative: false,
	decimal: ',',
	precision: 2,
	prefix: '',
	suffix: '',
	thousands: '.',
};

export const OfficeRxStompConfig: InjectableRxStompConfig = {
	// Which server?
	brokerURL: environment.rabbitmq.server,

	// Headers
	// Typical keys: login, passcode, host
	connectHeaders: {
		login: environment.rabbitmq.login,
		passcode: environment.rabbitmq.password,
		host: environment.rabbitmq.virtualHost,
	},

	// How often to heartbeat?
	// Interval in milliseconds, set to 0 to disable
	heartbeatIncoming: 0, // Typical value 0 - disabled
	heartbeatOutgoing: 20000, // Typical value 20000 - every 20 seconds

	// Wait in milliseconds before attempting auto reconnect
	// Set to 0 to disable
	// Typical value 500 (500 milli seconds)
	reconnectDelay: 200,

	// Will log diagnostics on console
	// It can be quite verbose, not recommended in production
	// Skip this key to stop logging to console
	debug: (msg): void => {
		if (
			!environment.production &&
			!window.location.hostname.includes('localhost')
		) {
			console.log(new Date(), msg);
		}
	},
};

@NgModule({
	declarations: [
		AccountComponent,
		AccountMobPaymentInfoComponent,
		AccountPaymentInfoComponent,
		AccountPremiumComponent,
		AdminLoginComponent,
		AppComponent,
		AppointmentsComponent,
		ArraySortPipe,
		AvailabilitiesComponent,
		CalendarComponent,
		ChartCompensationComponent,
		ChatComponent,
		ClientEventList,
		ClientsAddComponent,
		ClientsComponent,
		ClientsDetailsComponent,
		ClientSearch,
		ClientsEditComponent,
		DaycalendarComponent,
		DetailsComponent,
		FinancialReportLookerComponent,
		FinancialsComponent,
		FullDatePipe,
		GoogleCalendar,
		InfoIconTooltipComponent,
		InFullDatePipe,
		ListAvailabilitiesComponent,
		LoginComponent,
		LogoutComponent,
		LookerEmbedComponent,
		ManageAvailabilitiesComponent,
		MobAppointmentsManageComponent,
		MobAppointmentsOptionsComponent,
		MobAvailabilitiesEditComponent,
		MobAvailabilitiesListComponent,
		MobAvailabilitiesManageComponent,
		MonthcalendarComponent,
		Nl2brPipe,
		NotificationsComponent,
		ReasonsComponent,
		ShortTimePipe,
		SignatureEditorComponent,
		ValidationMessagesComponent,
		WeekcalendarComponent,
		CalendarFooterComponent,
		CalendarHeaderComponent,
		CalendarEventIconComponent,
		CalendarSlotRecurrencyAttemptComponent,
	],
	imports: [
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: true,
			registrationStrategy: 'registerImmediately',
		}),
		FreudSelectButtonModule,
		AngularFireAuthModule,
		AngularFireModule.initializeApp(environment.firebase),
		AuthInterceptorModule,
		AutocompleteLibModule,
		AutoCompleteModule,
		BrowserAnimationsModule,
		BrowserModule,
		BsDropdownModule.forRoot(),
		ButtonModule,
		CalendarModule,
		ChartModule,
		CheckoutModule,
		ContactsModule,
		CommonModule,
		CurrencyMaskModule,
		DropdownModule,
		FontAwesomeModule,
		FormsModule,
		FreudButtonModule,
		FreudLoaderModule,
		FreudTagModule,
		HttpClientModule,
		HttpClientModule,
		ImageCropperModule,
		InfiniteScrollModule,
		InputMaskModule,
		InputTextModule,
		MatDialogModule,
		ModalModule.forRoot(),
		MyAccountModule,
		Ng2CompleterModule,
		NgxDatatableModule,
		NgxMaskModule.forRoot(),
		NgxUnleashProxyClientModule.init(environment.unleash),
		OfficeIconModule,
		OfficeModalModule,
		OverlayPanelModule,
		ProfileCompletionModule,
		PromptuaryModule,
		ReactiveFormsModule,
		ReasonsModalModule,
		ReceiptButtonModule,
		RouterModule.forRoot(AppRoutes, {
			preloadingStrategy: PreloadAllModules,
			relativeLinkResolution: 'legacy',
		}),
		SignupModule,
		TableModule,
		ToastModule,
		TooltipModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		VideoConferenceModule,
		ZkButtonModule,
		ZkDialogModule,
		SafePipeModule,
		ZkIconModule,
		ZkInputModule,
		ZkLoaderModule,
		ZkNotificationModule,
		ZkTextModule,
		AppointmentSidebarModule,
		FreudToastModule,
		AddressParticularPriceModule,
		AddressCorporativePriceModule,
		FreudSplitButtonModule,
		ExperimentModule,
		HeaderModule,
		PromptuaryExportModule,
		FreudConfirmDialogModule,
		FreudTooltipModule,
		FreudAvatarModule,
		DetailsContactInfoModule,
		PromptuarySidebarModule,
		PromptuaryPendenciesModule,
		LottieModule.forRoot({ player: playerFactory }),
		AppointmentPreviewListModule,
		FreudRadioButtonModule,
		AppointmentTypeBadgeModule,
		SpeechToTextModule,
		CalendarRecurrencyAttemptModalModule,
		InsightsModule,
		FreudSwitchModule,
		AppointmentDetailsSidebarModule,
	],
	exports: [
		FinancialReportLookerComponent,
		LookerEmbedComponent,
		ValidationMessagesComponent,
	],
	providers: [
		ActionTrackerService,
		AddressService,
		ApiService,
		AuthenticationService,
		AuthGuard,
		AvailabilitiesService,
		CacheService,
		CalendarService,
		ChatService,
		CheckoutService,
		ContactsService,
		CurrencyService,
		DataLayerService,
		DateTimeService,
		DecimalPipe,
		ErrorService,
		FeedNotificationService,
		EventsService,
		GlobalService,
		HasAppointment,
		IdWallService,
		Location,
		LocationService,
		MessageService,
		MyAccountCanDeactivate,
		NetworkService,
		NotificationsService,
		SearchService,
		StatsService,
		StompService,
		StripeService,
		SubscriptionService,
		TaxonomyService,
		TriageService,
		UserService,
		WePayOutService,
		ZkuService,
		AddressService,
		{ provide: APP_BASE_HREF, useValue: '/' },
		{ provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
		{ provide: ErrorHandler, useClass: SentryErrorHandler },
		{ provide: InjectableRxStompConfig, useValue: OfficeRxStompConfig },
		{ provide: LocationStrategy, useClass: PathLocationStrategy },
		{
			provide: RxStompService,
			useFactory: rxStompServiceFactory,
			deps: [InjectableRxStompConfig],
		},
		SessionService,
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(private library: FaIconLibrary) {
		// SOLID
		library.addIcons(
			faAngleDown,
			faAngleLeft,
			faAngleRight,
			faAsterisk,
			faBars,
			faCalendarPlus,
			faCamera,
			faCheck,
			faCheckCircle,
			faCheckSquare,
			faChevronDown,
			faChevronLeft,
			faChevronRight,
			faCircle,
			faClock,
			faComments,
			faEnvelope,
			faExclamationCircle,
			faExclamationTriangle,
			faInfoCircle,
			faLock,
			faPaperPlane,
			faPauseCircle,
			faPlus,
			faPlusCircle,
			faSave,
			faSearch,
			faSortDown,
			faSortUp,
			faSync,
			faTimes,
			faTimesCircle,
			faToggleOff,
			faToggleOn,
			faUserCircle
		);

		// REGULAR
		library.addIcons(faFrown);
	}
}
